import React from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

function coverageColor(pct) {
  if (pct >= 90) return "#00ff90";
  if (pct >= 70) return "#fbbf24";
  return "#f87171";
}

export default function RosterCoverage({ loading, data }) {
  const slots = data?.slots || [];
  const gaps  = data?.gaps  || [];

  const chartData = slots.map(s => ({
    slot:      s.slot,
    scheduled: s.scheduled,
    required:  s.required,
    pct:       Math.min(100, Math.round((s.scheduled / Math.max(1, s.required)) * 100)),
  }));

  if (loading) return (
    <div className={`${card} p-5`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Roster & Shift Coverage</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Roster & Shift Coverage</span>
        {gaps.length > 0 && (
          <span className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-0.5 rounded-full">
            {gaps.length} gap{gaps.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="p-5 space-y-5">
        {slots.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-4">No roster data for the selected period.</p>
        ) : (
          <>
            {/* Bar chart */}
            <ResponsiveContainer width="100%" height={100}>
              <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                <XAxis dataKey="slot" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.35)" }} />
                <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} domain={[0, 100]} unit="%" />
                <Tooltip {...tooltipStyle} formatter={(v, n, p) => [
                  `${p.payload.scheduled}/${p.payload.required} (${v}%)`, "Coverage"
                ]} />
                <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={coverageColor(d.pct)} fillOpacity={0.8} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Progress rows */}
            <div className="space-y-2.5">
              {slots.map((s, i) => {
                const pct   = Math.min(100, Math.round((s.scheduled / Math.max(1, s.required)) * 100));
                const color = coverageColor(pct);
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-white/50">{s.slot}</span>
                      <span className="text-xs font-medium" style={{ color }}>{s.scheduled}/{s.required}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/5">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Gaps */}
            {gaps.length > 0 && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                <p className="text-xs text-red-400/60 uppercase tracking-wider mb-2">Largest Gaps</p>
                <ul className="space-y-1">
                  {gaps.map((g, i) => (
                    <li key={i} className="text-xs text-red-400/70">
                      {g.slot} — {g.dept}: <span className="font-medium">{g.gap} short</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}